import { FC, useContext, useEffect, useState } from "react";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { useMutation } from "react-query";
import client from "../../api";
import { BankCard, PaginationInput, User } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
} from "src/lib/constants";
import { capitalizeEachFirstLetter, getErrorMessage } from "src/lib/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { BankCardDataType } from "src/lib/constants/table-type";
import { BankCardIcon } from "src/components/icons/bank-card-icon";

type UserPaymentMethodsPageProps = {
  user: User;
};

// const getCardTypeName = (record: BankCardDataType): string => {
//   const combined = `${record.card_channel || ""} ${record.card_name || ""} ${
//     record.channel_name || ""
//   }`.toLowerCase();
//   if (combined.includes("credit")) {
//     return "Credit Card";
//   }
//   return "Debit Card";
// };

// const getCardStatus = (status?: string, is_verified?: number) => {
//   const normalized = (status || "").toLowerCase().trim();
//   if (
//     normalized === "verified" ||
//     normalized === "active" ||
//     is_verified === 1
//   ) {
//     return {
//       label: "Active",
//       dotColor: "bg-[#0B9630]",
//       badgeClass: "bg-[#E8F8EE] text-[#0B9630] border border-[#B7EBCA]",
//     };
//   }
//   if (normalized === "pending" || normalized === "authorized") {
//     return {
//       label: "Pending",
//       dotColor: "bg-[#F59E0B]",
//       badgeClass: "bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A]",
//     };
//   }
//   return {
//     label: capitalizeEachFirstLetter(status || "Inactive"),
//     dotColor: "bg-[#EF4444]",
//     badgeClass: "bg-[#FEE2E2] text-[#DC2626] border border-[#FECACA]",
//   };
// };

const UserPaymentMethodCards: FC<UserPaymentMethodsPageProps> = ({ user }) => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();

  const [bankCards, setBankCards] = useState<BankCard[]>([]);
  const [total, setTotal] = useState(0);
  const [dataSource, setDataSource] = useState<BankCardDataType[]>([]);

  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });

  const { mutate: getCards, isLoading: isIndividualCardsLoading } = useMutation(
    user.is_business
      ? client.users.getBusinessCardNumbers
      : client.users.getIndividualCardNumbers,
    {
      onSuccess: (data) => {
        setTotal(data.total);
        setBankCards(data.data);
        setDataSource(
          data.data.map((item) => ({
            key: item.id,
            id: item.id,
            card_name: item.card_name,
            bank_name: item.bank_name,
            card_number_last4: item.card_number_last4,
            holder_name: item.holder_name,
            status: item.status,
            card_icon: item.card_icon,
            card_channel: item.card_channel,
            channel_name: item.channel_name,
            is_verified: item.is_verified,
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({ ...searchParams, current_page: page });
  };

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    getCards({ id: user.id, params: searchParams });
  }, [isMounted, user, searchParams]);

  useEffect(() => {
    // isMounted && getPaymentMethods({ id: user.id, params: searchParams });
  }, [isMounted, searchParams.current_page]);

  const columns: ColumnsType<BankCardDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Card Details
        </p>
      ),
      key: "card_details",
      render: (_, record) => {
        const maskedNumber = record.card_number_last4
          ? `**** **** **** ${record.card_number_last4}`
          : "__";
        const cardTitle =
          capitalizeEachFirstLetter(record.card_name) || "Card";

        return (
          <div className="flex flex-row items-center gap-3">
            <div className="w-[48px] h-[32px] rounded-[6px] border border-[#E5E7EB] bg-white flex items-center justify-center p-1 shrink-0 overflow-hidden shadow-sm">
              {record.card_icon ? (
                <img
                  src={record.card_icon}
                  alt={cardTitle}
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback)
                      (fallback as HTMLElement).style.display = "block";
                  }}
                />
              ) : null}
              <span style={{ display: record.card_icon ? "none" : "block" }}>
                <BankCardIcon className="w-[20px] h-[20px] text-secondary" />
              </span>
            </div>
            <div className="flex flex-col">
              <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium leading-[20px]">
                {cardTitle}
              </p>
              <p className="font-manrope text-secondary text-[12px] sm:text-[13px] font-normal leading-[18px]">
                {maskedNumber}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Card Holder Name
        </p>
      ),
      dataIndex: "holder_name",
      key: "holder_name",
      render: (value: string) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value || "__"}
        </p>
      ),
    },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Card Type
    //     </p>
    //   ),
    //   key: "card_type",
    //   render: (_, record) => (
    //     <div className="flex flex-row items-center gap-2">
    //       <BankCardIcon className="w-[18px] h-[18px] text-secondary shrink-0" />
    //       <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
    //         {getCardTypeName(record)}
    //       </p>
    //     </div>
    //   ),
    // },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Status
    //     </p>
    //   ),
    //   dataIndex: "status",
    //   key: "status",
    //   render: (value: string, record) => {
    //     const cardStatus = getCardStatus(value, record.is_verified);
    //     return (
    //       <span
    //         className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] sm:text-[13px] font-medium ${cardStatus.badgeClass}`}
    //       >
    //         <span
    //           className={`w-1.5 h-1.5 rounded-full ${cardStatus.dotColor}`}
    //         />
    //         {cardStatus.label}
    //       </span>
    //     );
    //   },
    // },
  ];

  return (
    <Table
      loading={isIndividualCardsLoading}
      showSorterTooltip={false}
      columns={columns}
      dataSource={dataSource}
      pagination={{
        total,
        current: searchParams.current_page,
        onChange: onPageChange,
        position: ["bottomCenter"],
        defaultPageSize: LIST_PAGE_SIZE,
        showSizeChanger: false,
      }}
    />
  );
};

export default UserPaymentMethodCards;
